import { useState, useEffect, type FormEvent } from 'react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { DomainNodeList } from '@/components/domains/DomainNodeList';
import { useDomainDeployment } from '@/hooks/data/useDomainDeployment';
import { getApiErrorMessage } from '@/api/errors';
import { isValidDomainName } from '@/utils/validation';
import type {
  Domain,
  CreateDomainRequest,
  UpdateDomainRequest,
  CreateDomainResponse,
} from '@/types/domain';
import type { Node } from '@/types/node';
import type { OriginGroup } from '@/types/originGroup';
import './DomainModal.css';

/**
 * DomainModal component props
 */
export interface DomainModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Callback when the modal should close
   */
  onClose: () => void;

  /**
   * Create a domain (create mode)
   */
  onCreate: (data: CreateDomainRequest) => Promise<CreateDomainResponse>;

  /**
   * Update a domain (edit mode)
   */
  onUpdate: (id: string, data: UpdateDomainRequest) => Promise<Domain>;

  /**
   * Domain to edit (if editing mode)
   */
  domain?: Domain | null;

  /**
   * Available nodes for selection
   */
  nodes?: Node[];

  /**
   * Available origin groups for selection
   */
  originGroups?: OriginGroup[];
}

/**
 * Form validation errors
 */
interface FormErrors {
  name?: string;
  origin_group_id?: string;
}

/**
 * Check if two arrays have the same elements (order-insensitive)
 */
function sameElements(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedB = [...b].sort();
  return [...a].sort().every((val, idx) => val === sortedB[idx]);
}

/**
 * DomainModal component for creating and editing domains.
 *
 * Create/update go through `onCreate`/`onUpdate` (owned by the page); the
 * per-node deployment status and setup/import operations come from the
 * `useDomainDeployment` hook. Newly added nodes are set up automatically and
 * the modal stays open to show the progress.
 *
 * @example
 * ```tsx
 * <DomainModal
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onCreate={createDomain}
 *   onUpdate={updateDomain}
 *   domain={selectedDomain}
 *   nodes={nodes}
 *   originGroups={originGroups}
 * />
 * ```
 */
export function DomainModal({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  domain,
  nodes = [],
  originGroups = [],
}: DomainModalProps) {
  // Form state
  const [name, setName] = useState('');
  const [nodeIds, setNodeIds] = useState<string[]>([]);
  const [originGroupId, setOriginGroupId] = useState('');
  const [forceHttps, setForceHttps] = useState(true);
  const [includeWWW, setIncludeWWW] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  /** Node ids as last saved - used to detect newly added nodes */
  const [savedNodeIds, setSavedNodeIds] = useState<string[]>([]);
  /** Set once a new domain was created (the form is then finished) */
  const [createdDomainId, setCreatedDomainId] = useState<string | null>(null);

  const isEditMode = !!domain;
  const deployment = useDomainDeployment(isOpen && domain ? domain.id : null);

  // Reset form when modal opens/closes or domain changes
  useEffect(() => {
    if (!isOpen) return;

    setName(domain?.name ?? '');
    setNodeIds(domain?.node_ids ?? []);
    setSavedNodeIds(domain?.node_ids ?? []);
    setOriginGroupId(domain?.origin_group_id ?? '');
    setForceHttps(domain ? domain.force_https : true);
    setIncludeWWW(domain ? domain.include_www ?? domain.has_www_record ?? false : false);
    setErrors({});
    setSubmitError(null);
    setCreatedDomainId(null);
  }, [isOpen, domain]);

  /**
   * Validate the form
   */
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Name validation (only for create mode)
    if (!isEditMode) {
      if (!name.trim()) {
        newErrors.name = 'Domain name is required';
      } else if (!isValidDomainName(name.trim())) {
        newErrors.name = 'Invalid domain name format (e.g., example.com)';
      }
    }

    if (!originGroupId) {
      newErrors.origin_group_id = 'Origin Group is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Build the update payload with changed fields only
   */
  const buildUpdate = (current: Domain): UpdateDomainRequest => {
    const data: UpdateDomainRequest = {};
    if (!sameElements(nodeIds, savedNodeIds)) data.node_ids = nodeIds;
    if (originGroupId !== (current.origin_group_id || '')) data.origin_group_id = originGroupId;
    if (forceHttps !== current.force_https) data.force_https = forceHttps;
    if (includeWWW !== current.include_www) data.include_www = includeWWW;
    return data;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      if (domain) {
        const data = buildUpdate(domain);

        // Nothing changed - just close without calling the API
        if (Object.keys(data).length === 0) {
          onClose();
          return;
        }

        const newlyAddedNodes = nodeIds.filter((id) => !savedNodeIds.includes(id));
        await onUpdate(domain.id, data);
        setSavedNodeIds(nodeIds);

        if (newlyAddedNodes.length > 0) {
          // Keep the modal open so the user can watch the setup progress
          void deployment.setupNodes(domain.id, newlyAddedNodes);
          return;
        }
      } else {
        const data: CreateDomainRequest = {
          name: name.trim(),
          origin_group_id: originGroupId,
          force_https: forceHttps,
          include_www: includeWWW,
          ...(nodeIds.length > 0 ? { node_ids: nodeIds } : {}),
        };

        const created = await onCreate(data);

        if (nodeIds.length > 0) {
          // Keep the modal open so the user can watch the setup progress
          setCreatedDomainId(created.id);
          void deployment.setupNodes(created.id, nodeIds);
          return;
        }
      }

      onClose();
    } catch (error) {
      setSubmitError(getApiErrorMessage(error, 'Failed to save domain'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleNode = (nodeId: string, checked: boolean) => {
    setNodeIds((prev) => (checked ? [...prev, nodeId] : prev.filter((id) => id !== nodeId)));
  };

  // Get selected origin group for display
  const selectedOriginGroup = originGroups.find((og) => og.id === originGroupId);
  const isFinished = createdDomainId !== null;
  const formDisabled = isSaving || isFinished;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Domain' : 'Add Domain'}
      size="md"
      footer={
        isFinished ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" form="domain-form" isLoading={isSaving}>
              {isEditMode ? 'Update Domain' : 'Create Domain'}
            </Button>
          </>
        )
      }
    >
      <form id="domain-form" onSubmit={handleSubmit} className="domain-modal-form" noValidate>
        {submitError && (
          <div className="domain-modal-error" role="alert">
            {submitError}
          </div>
        )}

        {isEditMode ? (
          <div className="domain-modal-field">
            <span className="domain-modal-label">Domain Name</span>
            <div className="domain-modal-readonly">{domain?.name}</div>
          </div>
        ) : (
          <Input
            label="Domain Name"
            placeholder="Enter domain name (e.g., example.com)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={errors.name}
            required
            disabled={formDisabled}
          />
        )}

        <div className="domain-modal-field">
          <label htmlFor="domain-origin-group" className="domain-modal-label">
            Origin Group <span className="domain-modal-required">*</span>
          </label>
          <Select
            id="domain-origin-group"
            className={`domain-modal-select ${errors.origin_group_id ? 'domain-modal-select-error' : ''}`}
            value={originGroupId}
            onChange={setOriginGroupId}
            disabled={formDisabled}
            required
            placeholder="Select an origin group..."
            options={originGroups.map((og) => ({
              value: og.id,
              label: `${og.name} (${og.address}:${og.port})`,
            }))}
          />
          {errors.origin_group_id && (
            <span className="domain-modal-field-error">{errors.origin_group_id}</span>
          )}
          {selectedOriginGroup && (
            <div className="domain-modal-origin-info">
              <span className="domain-modal-origin-detail">
                <strong>Address:</strong> {selectedOriginGroup.address}:{selectedOriginGroup.port}
              </span>
              {selectedOriginGroup.enable_tls && (
                <span className="domain-modal-origin-badge domain-modal-origin-badge-tls">TLS</span>
              )}
              {selectedOriginGroup.health_status === 'healthy' && (
                <span className="domain-modal-origin-badge domain-modal-origin-badge-healthy">Healthy</span>
              )}
              {selectedOriginGroup.health_status === 'unhealthy' && (
                <span className="domain-modal-origin-badge domain-modal-origin-badge-unhealthy">Unhealthy</span>
              )}
            </div>
          )}
          {originGroups.length === 0 && (
            <span className="domain-modal-helper domain-modal-helper-warning">
              No origin groups available. Please create an origin group first.
            </span>
          )}
        </div>

        <div className="domain-modal-field">
          <span className="domain-modal-label">CDN Nodes</span>
          <DomainNodeList
            nodes={nodes}
            selectedNodeIds={nodeIds}
            onToggle={handleToggleNode}
            disabled={formDisabled}
            showDeployment={isEditMode}
            nodeStatuses={deployment.nodeStatuses}
            isLoadingStatuses={deployment.isLoadingStatuses}
            setupStates={deployment.setupStates}
            importStates={deployment.importStates}
            onSetup={deployment.setupNode}
            onImport={deployment.importCertificate}
          />
        </div>

        <div className="domain-modal-field">
          <label className="domain-modal-checkbox">
            <input
              type="checkbox"
              checked={forceHttps}
              onChange={(e) => setForceHttps(e.target.checked)}
              disabled={formDisabled}
            />
            <span className="domain-modal-checkbox-label">
              <strong>Force HTTPS</strong>
              <span className="domain-modal-checkbox-description">
                Redirect all HTTP requests to HTTPS
              </span>
            </span>
          </label>
        </div>

        <div className="domain-modal-field">
          <label className="domain-modal-checkbox domain-modal-www-option">
            <input
              type="checkbox"
              checked={includeWWW}
              onChange={(e) => setIncludeWWW(e.target.checked)}
              disabled={formDisabled}
            />
            <span className="domain-modal-checkbox-label">
              <strong>Include www.{name || 'domain'} in certificate</strong>
              <span className="domain-modal-checkbox-description">
                Issue SSL certificate for both {name || 'domain.com'} and www.{name || 'domain.com'}
              </span>
            </span>
          </label>
          {domain?.has_www_record && (
            <div className="domain-modal-www-dns-status">
              <span className="domain-modal-www-badge">www DNS record exists</span>
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
}
